import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useCatalog } from '../../context/CatalogContext'
import Mermaid from '../../components/Mermaid'

export default function WikiViewer() {
  // Support nested slugs via splat param "*"
  const params = useParams()
  const slug = (params['*'] as string) || params.slug
  const { catalog, loading, error: catError } = useCatalog()
  const [content, setContent] = useState<string>('')
  const [error, setError] = useState<string>('')
  // Sidebar state for sticky wiki nav
  const [sidebar, setSidebar] = useState<string>('')
  const [sbError, setSbError] = useState<string>('')

  // Base to fetch raw Markdown files from the GitHub Wiki at runtime
  const activeWiki = catalog?.wikis?.[0]
  const WIKI_REPO_RAW_BASE = activeWiki?.rawBase
  const WIKI_WEB_HOME = activeWiki?.webHome
  const HAS_WIKI = Boolean(WIKI_REPO_RAW_BASE)

  function normalizeWikiPage(input: string) {
    const trimmed = (input || '').replace(/^\/+|\/+$/g, '')
    const noMd = trimmed.replace(/\.md$/i, '')
    // GitHub Wiki convention uses dashes for spaces in filenames
    return noMd.replace(/\s+/g, '-')
  }

  function toWikiFileName(slugLike: string) {
    const name = normalizeWikiPage(slugLike)
    return `${encodeURIComponent(name)}.md`
  }

  async function loadWikiPage(slugLike: string): Promise<{ text: string; url: string } | null> {
    if (!WIKI_REPO_RAW_BASE) return null
    const file = toWikiFileName(slugLike)
    const url = `${WIKI_REPO_RAW_BASE}/${file}`
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) return null
      const text = await res.text()
      if (import.meta.env.DEV) console.debug('[wiki] loaded page from', url)
      return { text, url }
    } catch (_e) {
      return null
    }
  }

  // Try _Sidebar.md first, then Home.md as fallback for sidebar content
  async function loadWikiSidebar(): Promise<string | null> {
    if (!WIKI_REPO_RAW_BASE) return null
    const candidates = [`${WIKI_REPO_RAW_BASE}/_Sidebar.md`, `${WIKI_REPO_RAW_BASE}/Home.md`]
    for (const url of candidates) {
      try {
        const res = await fetch(url, { cache: 'no-cache' })
        if (!res.ok) continue
        const text = await res.text()
        if (import.meta.env.DEV) console.debug('[wiki] loaded sidebar from', url)
        return text
      } catch (_e) {
        // try next
      }
    }
    return null
  }

  // Convert wiki-style links [[Page Name]] into standard Markdown links pointing to /docs/wiki/<slug>
  function preprocessWikiSyntax(md: string) {
    if (!md) return md
    return md.replace(/\[\[([^\]]+)]]/g, (_m, p1: string) => {
      const target = normalizeWikiPage(p1)
      return `[${p1}](/docs/wiki/${target})`
    })
  }

  // ReactMarkdown link rendering: route relative wiki links inside the app under /docs/wiki
  const markdownComponents = {
    a({ href, children, ...props }: any) {
      const text = children as React.ReactNode
      const h = String(href || '')
      const isAbsolute = /^(https?:)?\/\//i.test(h)
      const isHash = h.startsWith('#')
      const isMailto = h.startsWith('mailto:')
      // Helper: rewrite absolute GitHub wiki links to in-app routes
      const rewriteAbsoluteWiki = (url: string): string | null => {
        try {
          const u = new URL(url)
          const host = u.host.toLowerCase()
          const path = u.pathname
          // Try to infer org/repo from configured webHome
          let webOrgRepo: RegExp | null = null
          try {
            if (WIKI_WEB_HOME) {
              const wh = new URL(WIKI_WEB_HOME)
              const m = /^\/(.+?)\/(.+?)\/wiki\/?$/.exec(wh.pathname)
              if (m) webOrgRepo = new RegExp(`^\/${m[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/wiki\/(.+)$`)
            }
          } catch (_e) {
          }

          // Web wiki URLs: https://github.com/<org>/<repo>/wiki/<Page>
          const webMatch = (webOrgRepo && webOrgRepo.exec(path)) || null
          if ((host === 'github.com' || host === 'www.github.com') && webMatch) {
            const page = decodeURIComponent(webMatch[1])
            const s = normalizeWikiPage(page).replace(/\.md$/i, '')
            const hash = u.hash || ''
            return `/docs/wiki/${s}${hash}`
          }
          // Raw wiki URLs: https://raw.githubusercontent.com/wiki/<org>/<repo>/<Page>.md
          let rawOrgRepo: RegExp | null = null
          try {
            if (WIKI_REPO_RAW_BASE) {
              const rb = new URL(WIKI_REPO_RAW_BASE)
              const m2 = /^\/wiki\/([^/]+)\/([^/]+)\/?$/.exec(rb.pathname)
              if (m2) rawOrgRepo = new RegExp(`^\/wiki\/${m2[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m2[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/(.+)\\.md$`)
            }
          } catch (_e) {
          }

          const rawMatch = (rawOrgRepo && rawOrgRepo.exec(path)) || null
          if (host === 'raw.githubusercontent.com' && rawMatch) {
            const page = decodeURIComponent(rawMatch[1])
            const s = normalizeWikiPage(page)
            const hash = u.hash || ''
            return `/docs/wiki/${s}${hash}`
          }
        } catch (_e) {
          // ignore
        }
        return null
      }

      if (!h || isHash || isMailto) {
        return (
          <a href={h} {...props}>
            {text}
          </a>
        )
      }

      if (isAbsolute) {
        const internal = rewriteAbsoluteWiki(h)
        if (internal) {
          return <Link to={internal}>{text}</Link>
        }
        // non-wiki absolute: keep external
        return (
          <a href={h} target="_blank" rel="noopener noreferrer" {...props}>
            {text}
          </a>
        )
      }

      // Internal wiki link: normalize and point to /docs/wiki/<page>
      const normalized = normalizeWikiPage(h)
      const noMd = normalized.replace(/\.md$/i, '')
      const route = `/docs/wiki/${noMd}`
      return <Link to={route}>{text}</Link>
    },
    code({ inline, className, children, ...props }: any) {
      const txt = String(children || '')
      const langMatch = /language-(\S+)/.exec(className || '')
      const lang = (langMatch && langMatch[1]) || ''
      if (!inline && (lang.toLowerCase() === 'mermaid' || /^mermaid\s*\n/.test(txt))) {
        const chart = txt.replace(/^mermaid\s*\n/, '')
        return <Mermaid chart={chart}/>
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },
  }

  const entry = useMemo(() => {
    if (!catalog || !slug) return undefined
    return catalog.docs.find((d) => d.slug === slug)
  }, [catalog, slug])

  useEffect(() => {
    setContent('')
    setError('')
    if (!slug) return
    if (!HAS_WIKI) {
      // Do not attempt network calls without configuration
      setError('No wiki configured. Please define wikis[0].rawBase in catalog.json.')
      return
    }

    ;(async () => {
      const result = await loadWikiPage(slug)
      if (result) setContent(preprocessWikiSyntax(result.text))
      else setError('Page not found on source (raw.githubusercontent.com/wiki)')
    })()
  }, [entry, slug, HAS_WIKI])

  // Load sidebar once
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!HAS_WIKI) {
        setSbError('No wiki configured. Please define wikis[0].rawBase in catalog.json.')
        return
      }
      const text = await loadWikiSidebar()
      if (!cancelled) {
        if (text) setSidebar(text)
        else setSbError('Sidebar not found on source (tried _Sidebar.md and Home.md).')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) return <p>Loading…</p>
  if (catError) return <div className="card" role="alert">Failed to load catalog: {catError}</div>

  return (
    <section className="section api-doc-page">
      <div className="docs-layout">
        <aside className="docs-sidebar">
          <div className="docs-sidebar-inner">
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>Integration & App Development</h3>
            {sbError && (
              <div className="card" role="alert">
                {sbError} {WIKI_WEB_HOME && (<a href={WIKI_WEB_HOME} target="_blank" rel="noreferrer">Open on GitHub</a>)}
              </div>
            )}
            {!sbError && !sidebar && <p>Loading…</p>}
            {!sbError && sidebar && (
              <div className="prose">
                <ReactMarkdown components={markdownComponents as any}>{sidebar}</ReactMarkdown>
              </div>
            )}
          </div>
        </aside>
        <div className="docs-content">
          <div className="back-row">
            <Link to="/docs/wiki" className="back-link">← Back to Integration & App Development</Link>
          </div>
          <div className="prose">
            <h1 className="page-title">{slug}</h1>
            {WIKI_WEB_HOME && (
              <p className="page-subtitle">Source: GitHub · <a href={WIKI_WEB_HOME} target="_blank" rel="noreferrer">open
                on GitHub</a></p>
            )}
            {error && (
              <div className="card" role="alert">
                <strong>Not found.</strong> {error}
              </div>
            )}
            {!error && content && (
              <ReactMarkdown components={markdownComponents as any}>{content}</ReactMarkdown>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
